import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const navItems = [
  { path: '/', label: 'Inicio' },
  { path: '/que-es-nfl', label: 'Qué es la NFL' },
  { path: '/reglas', label: 'Cómo se juega' },
  { path: '/posiciones', label: 'Posiciones' },
  { path: '/estrategia', label: 'Estrategia' },
  { path: '/blog', label: 'Blog' },
  { path: '/estadisticas', label: 'Estadísticas' },
  { path: '/historia', label: 'Historia' },
  { path: '/como-ver', label: 'Cómo ver NFL' },
  { path: '/glosario', label: 'Glosario' },
  { path: '/faq', label: 'FAQ' },
];

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-slate-900/95 backdrop-blur-xl border-b border-slate-800 shadow-lg' 
            : 'bg-transparent'
        }`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3 group"
              data-testid="nav-logo"
            >
              <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center transform transition-transform duration-300 group-hover:rotate-12">
                <span className="text-slate-900 font-black text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  NFL
                </span>
              </div>
              <div className="hidden sm:block">
                <span className="text-white font-bold text-lg tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  GUÍA NFL
                </span>
                <span className="text-emerald-400 text-xs block -mt-1 tracking-widest">
                  EN ESPAÑOL
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.slice(0, 6).map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link text-sm ${location.pathname === item.path ? 'active' : ''}`}
                  data-testid={`nav-link-${item.path.replace('/', '') || 'home'}`}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Dropdown for more items */}
              <div className="relative group">
                <button className="nav-link text-sm flex items-center gap-1">
                  Más
                  <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" />
                </button>
                <div className="absolute top-full right-0 mt-2 w-48 bg-slate-800 rounded-lg border border-slate-700 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform group-hover:translate-y-0 translate-y-2">
                  {navItems.slice(6).map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`block px-4 py-3 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg ${
                        location.pathname === item.path ? 'text-emerald-400 bg-slate-700/30' : ''
                      }`}
                      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-slate-300 hover:text-white transition-colors"
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div 
          className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-emerald-500 to-orange-500"
          style={{ 
            width: `${Math.min((navItems.findIndex(i => i.path === location.pathname) + 1) / navItems.length * 100, 100)}%`,
            transition: 'width 0.3s ease'
          }}
        />
      </nav>

      {/* Mobile menu */}
      <div 
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isOpen ? 'visible' : 'invisible'
        }`}
      >
        <div 
          className={`absolute inset-0 bg-slate-900/98 backdrop-blur-xl transition-opacity duration-300 ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsOpen(false)}
        />
        <div 
          className={`absolute top-20 left-4 right-4 bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl transform transition-all duration-300 ${
            isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
          }`}
        >
          <div className="p-4 space-y-1">
            {navItems.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block px-4 py-3 rounded-lg text-lg font-semibold transition-colors duration-200 ${
                  location.pathname === item.path 
                    ? 'text-emerald-400 bg-emerald-500/10' 
                    : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                }`}
                style={{ 
                  fontFamily: 'Barlow Condensed, sans-serif',
                  animationDelay: `${index * 50}ms`
                }}
                data-testid={`mobile-nav-link-${item.path.replace('/', '') || 'home'}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
